// frontend/src/features/livestock/SortNotice.tsx
// The assign view's word on what was just sorted (plan 4.4), inside the
// action bar on a phone and above the actions on a computer, rather than a
// notification over the page: "Assigned to Hammer Coral · Undo" for ten
// seconds, how many are still being sent, and a failure, with Retry and
// Open, until the user deals with it.

import { useEffect, useReducer } from 'react';
import { Alert, Button, Group, Text } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { BRAND_TEXT } from './brandText';
import { formatPhotoDay } from './photoInboxFormat';
import { describeAssignedElsewhere } from './photoInboxErrors';
import {
  canUndo,
  targetOf,
  useSortRecords,
  wasSortedBefore,
  type SortRecord,
} from './photoInboxSorting';
import { useSendingStatus, type InboxSorting } from './useInboxSorting';
import classes from './AssignPhotoView.module.css';

/** How long "Assigned to … · Undo" stays (the grid's Recently sorted keeps Undo longer). */
export const NOTICE_MS = 10_000;

const PHONE_CLOSE_BUTTON = {
  width: 44,
  height: 44,
  minWidth: 44,
  minHeight: 44,
  margin: '-14px -14px -14px 0',
};

export interface SortNoticeProps {
  tankId: string;
  sorting: InboxSorting;
  /** Computer: smaller buttons in one row. */
  compact?: boolean;
}

function sortedText(record: SortRecord): string {
  if (record.kind === 'discard') return 'Discarded';
  const name = record.resultLivestockName ?? record.livestockName ?? 'an organism';
  if (record.elsewhere) return describeAssignedElsewhere(targetOf(record), name);
  if (targetOf(record) === 'tank') return 'Added as tank shot';
  return wasSortedBefore(record) ? `Already assigned to ${name}` : `Assigned to ${name}`;
}

/** The latest change worth a line, while it is fresh. */
function freshest(records: SortRecord[], now: number): SortRecord | null {
  let latest: SortRecord | null = null;
  for (const record of records) {
    if (record.status !== 'done' && record.status !== 'gone' && record.status !== 'undoing') {
      continue;
    }
    if (now - record.changedAt >= NOTICE_MS) continue;
    if (!latest || record.changedAt > latest.changedAt) latest = record;
  }
  return latest;
}

export function SortNotice({
  tankId,
  sorting,
  compact = false,
}: SortNoticeProps): JSX.Element | null {
  const records = useSortRecords(tankId);
  const sending = useSendingStatus(tankId);
  const [, redraw] = useReducer((count: number) => count + 1, 0);
  const now = Date.now();
  const failure = records.find((record) => record.status === 'failed' && !record.dismissed);
  const fresh = freshest(records, now);

  // Hide the line when it goes stale.
  const expiresAt = fresh ? fresh.changedAt + NOTICE_MS : null;
  useEffect(() => {
    if (expiresAt === null) return undefined;
    const timer = window.setTimeout(redraw, Math.max(0, expiresAt - Date.now()) + 20);
    return () => window.clearTimeout(timer);
  }, [expiresAt]);

  if (failure) {
    const what = failure.kind === 'assign' ? 'assign' : 'discard';
    return (
      <Alert
        color="red"
        variant="light"
        p="xs"
        icon={<IconAlertCircle size={16} />}
        withCloseButton
        closeButtonLabel="Dismiss"
        onClose={() => sorting.dismiss(failure.key)}
        // A 44 px target on a phone, its corner kept where the 16 px one was.
        styles={compact ? undefined : { closeButton: PHONE_CLOSE_BUTTON }}
      >
        <Text size="sm">
          {`Couldn't ${what} the photo from ${formatPhotoDay(failure.takenAt)}. ${failure.message ?? ''}`}
        </Text>
        <Group gap="xs" mt={6} grow={!compact} wrap="nowrap">
          {failure.canRetry && (
            <Button
              variant="filled"
              color="red"
              size={compact ? 'compact-sm' : 'sm'}
              h={compact ? undefined : 44}
              fullWidth={!compact}
              onClick={() => sorting.retry(failure.key)}
            >
              Retry
            </Button>
          )}
          <Button
            variant="default"
            size={compact ? 'compact-sm' : 'sm'}
            h={compact ? undefined : 44}
            fullWidth={!compact}
            onClick={() => sorting.open(failure.key)}
          >
            Open
          </Button>
        </Group>
      </Alert>
    );
  }

  let line: JSX.Element | null = null;
  if (fresh?.status === 'undoing') {
    line = <Text size="sm">Undoing…</Text>;
  } else if (fresh?.status === 'gone') {
    line = (
      <Text size="sm" c="gray.7">
        {`The photo from ${formatPhotoDay(fresh.takenAt)} was ${fresh.message ?? 'sorted already'}.`}
      </Text>
    );
  } else if (fresh?.status === 'done' && fresh.undoMessage) {
    line = (
      <>
        <Text size="sm" c="red.8" className={classes.noticeText}>
          {fresh.undoMessage}
        </Text>
        {canUndo(fresh) && (
          <Button
            variant="subtle"
            size="compact-sm"
            h={compact ? undefined : 44}
            onClick={() => sorting.undo(fresh.key)}
            style={BRAND_TEXT}
          >
            Try again
          </Button>
        )}
      </>
    );
  } else if (fresh?.status === 'done') {
    line = (
      <>
        <Text size="sm" className={classes.noticeText}>
          {sortedText(fresh)}
        </Text>
        {canUndo(fresh) && (
          <Button
            variant="subtle"
            size="compact-sm"
            h={compact ? undefined : 44}
            onClick={() => sorting.undo(fresh.key)}
            style={BRAND_TEXT}
          >
            Undo
          </Button>
        )}
      </>
    );
  }

  if (!line && !sending.label) return null;
  return (
    <Group
      gap="xs"
      wrap="nowrap"
      justify="space-between"
      className={classes.notice}
      data-compact={compact || undefined}
    >
      <Group gap={4} wrap="nowrap" style={{ minWidth: 0 }} role="status">
        {line}
      </Group>
      {sending.label && (
        <Text size="xs" c="gray.7" className={classes.sending}>
          {sending.label}
        </Text>
      )}
    </Group>
  );
}
